import {
  API_PREFIX_BANNER_PATH,
  API_PREFIX_EVENT_PATH,
  API_PREFIX_POST_PATH,
  API_PREFIX_WEBSITE_PATH,
} from "@/constants/prefix-path-api"
import { message } from "@/lib/config/lib/antd-global"
import { t } from "i18next"
import { useState } from "react"
import { HttpClient } from "../../../../app/api/HttpClient"

// posts/banners/events must NOT be populated on the website record: Strapi v4
// silently ignores `populate[posts][pagination]`, so the edit screen pulled the
// whole set back — tamdamedia.eu links 62k+ posts, i.e. a 7.5 MB response and
// 62k antd tags to render. We read them from the child collection instead,
// where top-level pagination is honoured.
export const WEBSITE_RELATION_SOURCES = {
  posts: { path: API_PREFIX_POST_PATH, filterKey: "websites" },
  banners: { path: API_PREFIX_BANNER_PATH, filterKey: "website" },
  events: { path: API_PREFIX_EVENT_PATH, filterKey: "websites" },
} as const

export type WebsiteRelation = keyof typeof WEBSITE_RELATION_SOURCES

/** ids of each big relation that the form actually loaded — see buildRelationDelta. */
export type WebsiteRelationBaseline = Record<WebsiteRelation, number[]>

export const WEBSITE_RELATION_PAGE_SIZE = 20

export const createRelationBaseline = (): WebsiteRelationBaseline => ({
  posts: [],
  banners: [],
  events: [],
})

export const fetchWebsiteRelationPage = async (
  websiteId: number,
  relation: WebsiteRelation,
  page: number,
  pageSize: number = WEBSITE_RELATION_PAGE_SIZE,
): Promise<{ data: any[]; total: number }> => {
  const source = WEBSITE_RELATION_SOURCES[relation]

  try {
    const response = await HttpClient.get(source.path, {
      // We already scope by website id; the admin domain filter would fight it
      // when editing a site other than the currently selected one.
      skipDomainFilter: true,
      params: {
        fields: ["name"],
        publicationState: "preview",
        sort: "id:desc",
        filters: { [source.filterKey]: { id: { $eq: websiteId } } },
        pagination: { page, pageSize },
      },
    })

    return {
      data: response?.data ?? [],
      total: response?.meta?.pagination?.total ?? 0,
    }
  } catch (error) {
    message.error(t("Không thể tải thêm dữ liệu. Vui lòng thử lại"))
    return { data: [], total: 0 }
  }
}

type WebsiteListQueryParams = {
  fields?: string[]
  sort?: string
  filters?: Record<string, any>
  pagination?: Record<string, string>
}

type WebsiteByIdQueryParams = {
  fields?: string[]
  populate?: string | Record<string, any>
}

const DEFAULT_WEBSITE_BY_ID_QUERY_PARAMS: WebsiteByIdQueryParams = {
  populate: "*",
}

const websiteService = () => {
  const [isLoading, setIsLoading] = useState(false)

  const addWebsite = async (data: { data: any }) => {
    setIsLoading(true)
    try {
      const response = await HttpClient.post(`${API_PREFIX_WEBSITE_PATH}`, data)
      return response
    } catch (error) {
      message.error(t("Không thể thêm website. Vui lòng thử lại"))
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const fetchWebsiteById = async (
    id: number,
    queryParams: WebsiteByIdQueryParams = DEFAULT_WEBSITE_BY_ID_QUERY_PARAMS,
  ) => {
    setIsLoading(true)

    if (!id) {
      message.error(t("Id không hợp lệ"))
      setIsLoading(false)
      return null
    }

    try {
      const response = await HttpClient.get(`${API_PREFIX_WEBSITE_PATH}/${id}`, { params: queryParams })
      return response?.data || null
    } catch (error) {
      message.error(t("Lỗi khi tải dữ liệu website. Vui lòng thử lại"))
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const updateWebsite = async (id: number, data: { data: any }) => {
    setIsLoading(true)

    if (!id) {
      message.error(t("Id không hợp lệ"))
      setIsLoading(false)
      return null
    }

    try {
      const response = await HttpClient.put(`${API_PREFIX_WEBSITE_PATH}/${id}`, data)
      message.success("Website được cập nhật thành công!")
      return response
    } catch (error) {
      message.error(t("Không thể cập nhật website. Vui lòng thử lại"))
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const deleteWebsite = async (id: number) => {
    setIsLoading(true)

    if (!id) {
      message.error(t("Id không hợp lệ"))
      setIsLoading(false)
      return null
    }

    try {
      const response = await HttpClient.delete(`${API_PREFIX_WEBSITE_PATH}/${id}`)
      return response
    } catch (error) {
      message.error(t("Không thể xóa website. Vui lòng thử lại"))
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const fetchAllWebsites = async (searchTerms: { [key: string]: string } = {}) => {
    const params: WebsiteListQueryParams = {
      // Includes updatedAt/publishedAt too: both are optional "Configure view"
      // columns (see websiteColumnsRegistry) — omitting them here means the
      // column renders forever blank once a user turns it on.
      fields: ["name", "domain", "createdAt", "updatedAt", "publishedAt"],
      sort: "createdAt:desc",
      filters: {},
      pagination: {
        page: "1",
        pageSize: "20",
      },
    }

    const paginationKeys = ["page", "pageSize", "start", "limit"]

    Object.keys(searchTerms).forEach(key => {
      const value = searchTerms[key]
      if (!value) return

      if (paginationKeys.includes(key)) {
        params.pagination = {
          ...params.pagination,
          [key]: value.toString(),
        }
      } else if (key === "sort") {
        params.sort = value
      } else {
        params.filters![key] = { $containsi: value }
      }
    })

    try {
      const response = await HttpClient.get(`${API_PREFIX_WEBSITE_PATH}`, { params })
      return {
        data: response.data,
        meta: response.meta,
      }
    } catch (error) {
      message.error(t("Lỗi khi tải danh sách website. Vui lòng thử lại"))
    }
  }

  return {
    isLoading,
    addWebsite,
    fetchWebsiteById,
    fetchWebsiteRelationPage,
    updateWebsite,
    deleteWebsite,
    fetchAllWebsites,
  }
}

export default websiteService
